import { useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AddressSuggestionType, AddressType } from '@uplift/types';
import { useMemo, useState } from 'react';
import { BaseTextField } from '../../Inputs';
import { Modal, ModalContent } from '../../Modal';
import { AddressSearchList } from '../AddressSearchList/AddressSearchList';
import { useFetchAddressSuggestions } from './use-fetch-address-suggestions';

export type AddressSearchModalTypes = {
  onClose: () => void;
  onManualSelect: () => void;
  onAddressSelect: (arg0: AddressType) => void;
  open: boolean;
  color?: 'primary' | 'secondary';
};

export const ADDRESS_SEARCH_MODAL_ID = 'address-search-modal';

export const AddressSearchModal: React.FC<AddressSearchModalTypes> = ({
  onClose,
  open,
  onManualSelect,
  onAddressSelect,
  color = 'primary'
}) => {
  const { classes: modalClasses } = useModalStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [ searchValue, setSearchValue ] = useState('');
  const searchValueTransformed = useMemo(() => searchValue.trim().replace(/[^A-Za-z0-9 ]/g, ''), [ searchValue ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // trim and replace all non-alphaNumeric chars (that aren't spaces)
    setSearchValue(e.currentTarget.value ?? '');
  };

  const showResults: boolean = useMemo(
    () => searchValue.length >= 6 && searchValueTransformed.length >= 1,
    [ searchValueTransformed, searchValue ]
  );

  const { suggestions, loading } = useFetchAddressSuggestions({
    text: searchValueTransformed,
    constraint: showResults,
    debounceMs: 400
  });

  const addressSelectHandler = (rowId: string) => {
    const addressSuggestion: AddressSuggestionType = suggestions[Number(rowId)];
    const address = addressSuggestion.address;

    setSearchValue('');
    onAddressSelect(address);
  };

  const manualSelectHandler = () => {
    setSearchValue('');
    onManualSelect();
  };

  const closeModalHandler = () => {
    setSearchValue('');
    onClose();
  };

  const rows = useMemo(
    () => suggestions.map(({ address: { address1, address2, city, state, zip } }, i) => ({
      id: `${i}`,
      address: `${address1 ?? ''} ${address2 ?? ''}`,
      addressCityState: `${city ?? ''} ${state ?? ''} ${zip ?? ''}`
    })),
    [ suggestions ]
  );

  return (
    <Modal
      title=""
      onClose={closeModalHandler}
      open={open}
      maxWidth="md"
      classes={modalClasses}
      fullWidth={isSmall}
      fullScreen={isSmall}
      slotProps={{
        paper: {
          id: ADDRESS_SEARCH_MODAL_ID,
        }
      }}
    >
      <ModalContent>
        <BaseTextField
          autoComplete="off"
          autoFocus
          fullWidth
          label="Address"
          name="quick-search-input"
          color={color}
          onChange={handleSearchChange}
          value={searchValue}
        />
        <AddressSearchList
          loading={loading}
          rows={rows}
          onAddressSelect={addressSelectHandler}
          onManualSelect={manualSelectHandler}
          showResults={showResults}
          searchValue={searchValue}
        />
      </ModalContent>
    </Modal>
  );
};

const useModalStyles = makeStyles()({
  paper: {
    margin: 0,
    height: '550px'
  },
  paperWidthMd: {
    width: '600px',
    maxWidth: '600px'
  },
  paperFullWidth: {
    width: '100%',
    maxWidth: 'initial',
  },
  paperFullScreen: {
    height: '100%'
  }
});
